import { parseStructuredReportFromOutput } from '@apps/web/components/phoebe_chat_view/artifacts/structured_report_artifacts';
import { stripUntrustedMarkers } from '@apps/web/routes/_app/admin/agent/artifacts/shared';
import {
    type AdminToolOutputRenderSource,
    type AdminToolOutputSelection,
    getFilenameHint,
    getKindHint,
    getLanguageHint,
    getStringField,
    inferLanguage,
    inferLanguageFromFilename,
    isDiffLikeText,
    isRecord,
    looksLikeCode,
    MAX_TABLE_COLUMNS,
    parseCodeFence,
    type ParsedDiffFile,
    type ParsedDiffHunk,
    type ParsedDiffLine,
    type ParsedDiffLineKind,
    parseJsonString,
    toDisplayString,
} from '@apps/web/routes/_app/admin/agent/tool_output_renderer_shared';

export type AdminToolOutputMatch = {
    readonly selection: AdminToolOutputSelection;
    readonly source: 'output' | 'text';
    readonly keepTextSummary: boolean;
};

function stripDisplayEnvelope(
    value: Record<string, unknown>,
): Record<string, unknown> {
    const nextValue = { ...value };
    delete nextValue['truncation_markers'];
    return nextValue;
}

function consoleTextField(
    record: Record<string, unknown>,
    ...keys: ReadonlyArray<string>
): string | null {
    for (const key of keys) {
        const value = record[key];
        if (typeof value === 'string') {
            return stripUntrustedMarkers(value);
        }
    }
    return null;
}

// Process-style tool results (sandbox commands)
// carry an exit code plus stdout/stderr-like text. A human reads the
// output and the outcome chips; hashes, rlimits, and audit metadata stay
// behind the raw-payload toggle.
function selectConsoleRecord(
    record: Record<string, unknown>,
): AdminToolOutputSelection | null {
    const exitCode = record['exit_code'];
    if (typeof exitCode !== 'number') {
        return null;
    }
    const stdout = consoleTextField(
        record,
        'stdout_preview',
        'stdout',
        'output_preview',
        'output',
    );
    const stderr = consoleTextField(record, 'stderr_preview', 'stderr');
    if (stdout === null && stderr === null) {
        return null;
    }
    return {
        kind: 'console',
        stdout: stdout ?? '',
        stderr: stderr ?? '',
        exitCode,
        durationMs:
            typeof record['duration_ms'] === 'number'
                ? record['duration_ms']
                : null,
        stepName:
            typeof record['step_name'] === 'string'
                ? record['step_name']
                : null,
        timedOut: record['timed_out'] === true,
        outputTruncated:
            record['stdout_truncated'] === true ||
            record['stderr_truncated'] === true ||
            record['output_truncated'] === true,
        raw: record,
    };
}

function selectFromStructuredRecord({
    record,
    fallbackFilename,
}: {
    readonly record: Record<string, unknown>;
    readonly fallbackFilename: string | null;
}): AdminToolOutputSelection | null {
    const kindHint = getKindHint(record);
    const languageHint = getLanguageHint(record);
    const filename = getFilenameHint(record, fallbackFilename);
    const textValue =
        getStringField(record, 'content', 'text', 'value', 'patch', 'diff') ??
        null;

    if (kindHint === 'table') {
        const parsed = parseStructuredReportFromOutput(record);
        if (parsed) {
            return {
                kind: 'table',
                columns: parsed.columns,
                rows: parsed.rows,
                summaryLabel: `${parsed.summary.returned.toLocaleString()} row${
                    parsed.summary.returned === 1 ? '' : 's'
                }`,
            };
        }
    }

    if (kindHint === 'diff' && textValue) {
        return {
            kind: 'diff',
            text: textValue,
            files: parseUnifiedDiff(textValue),
        };
    }

    if (kindHint === 'code' && textValue) {
        return {
            kind: 'code',
            code: textValue,
            language: inferLanguage({
                explicitLanguage: languageHint,
                filename,
                text: textValue,
            }),
            filename,
        };
    }

    if (kindHint === 'json') {
        return {
            kind: 'json',
            value: record,
        };
    }

    if (kindHint === 'markdown' && textValue) {
        return {
            kind: 'markdown',
            markdown: textValue,
        };
    }

    return null;
}

function selectArrayTable(
    value: ReadonlyArray<unknown>,
): AdminToolOutputSelection | null {
    if (value.length === 0 || !value.every(isRecord)) {
        return null;
    }
    const columns: Array<string> = [];
    for (const row of value) {
        for (const key of Object.keys(row)) {
            if (!columns.includes(key)) {
                columns.push(key);
            }
        }
        if (columns.length >= MAX_TABLE_COLUMNS) {
            break;
        }
    }
    if (columns.length === 0) {
        return null;
    }
    return {
        kind: 'table',
        columns,
        rows: value.map((row) =>
            columns.map((column) =>
                toDisplayString((row as Record<string, unknown>)[column]),
            ),
        ),
        summaryLabel: `${value.length.toLocaleString()} row${
            value.length === 1 ? '' : 's'
        }`,
    };
}

function selectParsedJsonContent(
    value: unknown,
): AdminToolOutputSelection | null {
    const structuredReport = parseStructuredReportFromOutput(value);
    if (structuredReport) {
        return {
            kind: 'table',
            columns: structuredReport.columns,
            rows: structuredReport.rows,
            summaryLabel: `${structuredReport.summary.returned.toLocaleString()} row${
                structuredReport.summary.returned === 1 ? '' : 's'
            }`,
        };
    }

    if (Array.isArray(value)) {
        return selectArrayTable(value);
    }

    return {
        kind: 'json',
        value,
    };
}

function selectStringContent({
    text,
    explicitLanguage = null,
    filename = null,
}: {
    readonly text: string;
    readonly explicitLanguage?: string | null;
    readonly filename?: string | null;
}): AdminToolOutputSelection | null {
    if (!text.trim()) {
        return null;
    }

    const fenced = parseCodeFence(text);
    if (fenced) {
        const language = inferLanguage({
            explicitLanguage: fenced.language ?? explicitLanguage,
            filename,
            text: fenced.code,
        });
        if (language === 'diff' || isDiffLikeText(fenced.code, language)) {
            return {
                kind: 'diff',
                text: fenced.code,
                files: parseUnifiedDiff(fenced.code),
            };
        }
        return {
            kind: 'code',
            code: fenced.code,
            language,
            filename,
        };
    }

    if (isDiffLikeText(text, explicitLanguage)) {
        return {
            kind: 'diff',
            text,
            files: parseUnifiedDiff(text),
        };
    }

    const parsedJson = parseJsonString(text);
    if (parsedJson !== null) {
        return selectParsedJsonContent(parsedJson);
    }

    if (looksLikeCode(text, explicitLanguage)) {
        return {
            kind: 'code',
            code: text,
            language: inferLanguage({
                explicitLanguage,
                filename,
                text,
            }),
            filename,
        };
    }

    return {
        kind: 'markdown',
        markdown: text,
    };
}

export function selectAdminToolOutputMatch(
    source: AdminToolOutputRenderSource,
): AdminToolOutputMatch | null {
    const textSelection = source.text
        ? selectStringContent({
              text: source.text,
              filename: source.filename ?? null,
          })
        : null;
    const structuredReport = parseStructuredReportFromOutput(source.output);
    if (structuredReport) {
        return {
            selection: {
                kind: 'table',
                columns: structuredReport.columns,
                rows: structuredReport.rows,
                summaryLabel: `${structuredReport.summary.returned.toLocaleString()} row${
                    structuredReport.summary.returned === 1 ? '' : 's'
                }`,
            },
            source: 'output',
            keepTextSummary: textSelection?.kind === 'markdown',
        };
    }

    const outputValue = isRecord(source.output)
        ? stripDisplayEnvelope(source.output)
        : source.output;
    if (Array.isArray(outputValue)) {
        const tableSelection = selectArrayTable(outputValue);
        if (tableSelection) {
            return {
                selection: tableSelection,
                source: 'output',
                keepTextSummary: textSelection?.kind === 'markdown',
            };
        }
    }

    if (isRecord(outputValue)) {
        const hintedSelection = selectFromStructuredRecord({
            record: outputValue,
            fallbackFilename: source.filename ?? null,
        });
        if (hintedSelection) {
            return {
                selection: hintedSelection,
                source: 'output',
                keepTextSummary: textSelection?.kind === 'markdown',
            };
        }
        const consoleSelection = selectConsoleRecord(outputValue);
        if (consoleSelection) {
            return {
                selection: consoleSelection,
                source: 'output',
                keepTextSummary: false,
            };
        }
        const outputSelection: AdminToolOutputSelection = {
            kind: 'json',
            value: outputValue,
        };
        if (
            textSelection &&
            (textSelection.kind === 'code' || textSelection.kind === 'diff')
        ) {
            return {
                selection: textSelection,
                source: 'text',
                keepTextSummary: false,
            };
        }
        return {
            selection: outputSelection,
            source: 'output',
            keepTextSummary: textSelection?.kind === 'markdown',
        };
    }

    if (typeof outputValue === 'string') {
        const outputSelection = selectStringContent({
            text: outputValue,
            filename: source.filename ?? null,
        });
        if (
            outputSelection &&
            (outputSelection.kind === 'code' ||
                outputSelection.kind === 'diff' ||
                outputSelection.kind === 'json' ||
                outputSelection.kind === 'table')
        ) {
            return {
                selection: outputSelection,
                source: 'output',
                keepTextSummary: textSelection?.kind === 'markdown',
            };
        }
    }

    if (textSelection) {
        return {
            selection: textSelection,
            source: 'text',
            keepTextSummary: false,
        };
    }

    if (typeof outputValue === 'string') {
        const outputSelection = selectStringContent({
            text: outputValue,
            filename: source.filename ?? null,
        });
        return outputSelection
            ? {
                  selection: outputSelection,
                  source: 'output',
                  keepTextSummary: false,
              }
            : null;
    }

    return outputValue === null || outputValue === undefined
        ? null
        : {
              selection: {
                  kind: 'json',
                  value: outputValue,
              },
              source: 'output',
              keepTextSummary: false,
          };
}

export function selectAdminToolOutput(
    source: AdminToolOutputRenderSource,
): AdminToolOutputSelection | null {
    return selectAdminToolOutputMatch(source)?.selection ?? null;
}

function normalizeDiffPath(rawPath: string): string {
    return rawPath.replace(/^[ab]\//, '').trim();
}

function diffFileLabel({
    newPath,
    oldPath,
    fallbackIndex,
}: {
    readonly newPath: string | null;
    readonly oldPath: string | null;
    readonly fallbackIndex: number;
}): string {
    if (newPath && oldPath && newPath !== oldPath) {
        return `${newPath} ← ${oldPath}`;
    }
    if (newPath || oldPath) {
        return newPath ?? oldPath ?? `Patch ${fallbackIndex + 1}`;
    }
    return `Patch ${fallbackIndex + 1}`;
}

function diffLineKind(line: string): ParsedDiffLineKind {
    if (line.startsWith('+') && !line.startsWith('+++ ')) {
        return 'add';
    }
    if (line.startsWith('-') && !line.startsWith('--- ')) {
        return 'remove';
    }
    if (line.startsWith(' ')) {
        return 'context';
    }
    return 'meta';
}

export function parseUnifiedDiff(text: string): ReadonlyArray<ParsedDiffFile> {
    const lines = text.split('\n');
    const files: Array<ParsedDiffFile> = [];
    let currentOldPath: string | null = null;
    let currentNewPath: string | null = null;
    let currentHunks: Array<ParsedDiffHunk> = [];
    let currentLines: Array<ParsedDiffLine> = [];
    let currentHeader = '@@';

    const commitCurrentHunk = () => {
        if (currentLines.length === 0) {
            return;
        }
        currentHunks.push({
            header: currentHeader,
            lines: currentLines,
        });
        currentLines = [];
    };

    const commitCurrentFile = () => {
        commitCurrentHunk();
        if (currentHunks.length === 0) {
            return;
        }
        const label = diffFileLabel({
            newPath: currentNewPath,
            oldPath: currentOldPath,
            fallbackIndex: files.length,
        });
        files.push({
            label,
            language: inferLanguageFromFilename(
                currentNewPath ?? currentOldPath,
            ),
            hunks: currentHunks,
        });
        currentOldPath = null;
        currentNewPath = null;
        currentHunks = [];
        currentHeader = '@@';
    };

    for (const line of lines) {
        if (line.startsWith('diff --git ')) {
            commitCurrentFile();
            const match = line.match(/^diff --git a\/(.+) b\/(.+)$/);
            currentOldPath = match?.[1] ?? null;
            currentNewPath = match?.[2] ?? null;
            continue;
        }
        if (line.startsWith('--- ')) {
            const rawPath = line.slice(4).trim();
            currentOldPath =
                rawPath === '/dev/null' ? null : normalizeDiffPath(rawPath);
            continue;
        }
        if (line.startsWith('+++ ')) {
            const rawPath = line.slice(4).trim();
            currentNewPath =
                rawPath === '/dev/null' ? null : normalizeDiffPath(rawPath);
            continue;
        }
        if (line.startsWith('@@ ')) {
            commitCurrentHunk();
            currentHeader = line;
            continue;
        }
        currentLines.push({
            kind: diffLineKind(line),
            text: line,
        });
    }

    commitCurrentFile();

    if (files.length === 0) {
        return [
            {
                label: 'Patch',
                language: null,
                hunks: [
                    {
                        header: '@@',
                        lines: lines.map((line) => ({
                            kind: diffLineKind(line),
                            text: line,
                        })),
                    },
                ],
            },
        ];
    }

    return files;
}
